import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { Link } from '@dx-ui/osc-link';
import { JoinModal } from '@dx-ui/osc-join-modal-button';
import { Popup } from '@dx-ui/osc-popup';
import { LoginForm } from '@dx-ui/osc-login';
import { HEADER_CLICK_IDS } from '../types';
import type { MegaMenuProps } from '../types';
import {
  dispatchGlobalNavigationClick,
  dispatchGlobalNavigationClickBasedOnLabel,
  sendInteractionReward,
  SpeechBubbleContext,
} from '../header.utilities';
import { LanguageSelector } from '@dx-ui/osc-language-selector';
import { sendReward } from '@dx-ui/framework-conductrics';
import { HeaderLogoLink, Logo, getLogoVariant } from '../header.logo';
import { useContext, useRef } from 'react';
import {
  NavItem,
  NavItemVisual,
  NavItemWithLink,
  NavItemWithModal,
  NavItemWithPopup,
  NavItemWithSpeechBubble,
} from './nav-items';
import UserPopupContents, { NavigationPopupContents } from './popup-contents';
import { useRouter } from 'next/router';
import { replaceLang } from '@dx-ui/framework-i18n';
import { GlobalNavMenu } from '../globalNavMenuDesktop';

const mapBrandCodeToSubmenuSurface = (brandCode: string) => {
  if (brandCode === 'ID') return 'alternate';
  if (brandCode === 'OL') return 'contrast';
  return 'base';
};
export function MegaMenu({
  isFluid,
  suppressLogo = false,
  shouldSuppressHonorsAccountLinks,
  shouldSuppressFindStay,
  shouldSuppressJoinAndSignIn,
  shouldOpenAccountLinksInNewWindow,
  shouldOpenJoinInNewWindow,
  shouldOpenJoinInPage,
  brand,
  mainNavLinks,
  user,
  languageSelectorOptions,
  loginOptions,
  onSignInAttempt,
  userLinks,
  onSignOut,
  heading,
  mvts,
  surfaceType,
  globalNavLinks,
  isTranslucent,
  isHamburgerNavVariant,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
  analytics,
}: MegaMenuProps) {
  const userNavProps = {
    user,
    userLinks,
    onSignInAttempt,
    onSignOut,
    loginOptions,
    languageSelectorOptions,
    mvts,
    shouldSuppressHonorsAccountLinks,
    shouldSuppressFindStay,
    shouldSuppressJoinAndSignIn,
    shouldOpenAccountLinksInNewWindow,
    shouldOpenJoinInNewWindow,
    shouldOpenJoinInPage,
    analytics,
    brand,
    isTranslucent,
    onGlobalNavigationClick,
    isLegacyGlobalNavigationClickAnalyticsEnabled,
  };
  const headingLevelProps = heading
    ? {
        role: 'heading',
        'aria-level': 1,
        'aria-label': heading,
      }
    : {};

  const hasMainNavLinks = Array.isArray(mainNavLinks) && mainNavLinks.length > 0;

  const shouldShowLogoOnTopLine = !isHamburgerNavVariant || !hasMainNavLinks;

  return (
    <>
      <div className="w-full">
        <div
          className={cx('osc-nav-top-link-area', {
            'container-fluid': isFluid,
            container: !isFluid,
          })}
        >
          <div
            className={cx('relative flex flex-wrap', {
              'items-center': isHamburgerNavVariant,
            })}
          >
            {suppressLogo ? null : (
              <div className="flex min-h-[4.5rem] items-center">
                <Popup key="globalNav">
                  <div className="z-10">
                    {isHamburgerNavVariant && globalNavLinks?.length && (
                      <GlobalNavMenu
                        surfaceType={surfaceType}
                        isTranslucent={isTranslucent}
                        globalNavLinks={globalNavLinks}
                      />
                    )}
                  </div>
                </Popup>
                {shouldShowLogoOnTopLine ? (
                  <div {...headingLevelProps}>
                    <Link
                      url={brand.url}
                      target={brand.target}
                      onClick={sendInteractionReward}
                      // once nav is implemented on all pages for OHW make w-72 for all brands per UX/UI
                      anchorClassName="relative block h-14 w-28 brand-ee:lg:w-44 brand-ej:lg:w-44 brand-ou:lg:w-72 brand-lx:lg:w-72 brand-yh:lg:w-72 me-1"
                      showNewWindowIcon={false}
                      data-testid="brand-logo-link"
                    >
                      {!suppressLogo ? (
                        <Logo variant={getLogoVariant(surfaceType)} brand={brand} />
                      ) : null}
                    </Link>
                  </div>
                ) : null}
              </div>
            )}
            <div className="absolute end-0 top-0">
              <UserNav {...userNavProps} />
            </div>
          </div>
        </div>
      </div>
      <div className={cx({ 'osc-nav-bottom-link-area': !isTranslucent })}>
        <div
          className={cx({
            'container-fluid': isFluid,
            container: !isFluid,
          })}
        >
          <div className="flex">
            <div className="flex overflow-auto" data-testid="header-links-container">
              {hasMainNavLinks ? (
                <MainNav
                  brand={brand}
                  mainNavLinks={mainNavLinks}
                  isHamburgerNavVariant={isHamburgerNavVariant}
                  heading={heading}
                  surfaceType={surfaceType}
                  isTranslucent={isTranslucent}
                  onGlobalNavigationClick={onGlobalNavigationClick}
                  isLegacyGlobalNavigationClickAnalyticsEnabled={
                    isLegacyGlobalNavigationClickAnalyticsEnabled
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

type UserNavProps = Pick<
  MegaMenuProps,
  | 'loginOptions'
  | 'onSignInAttempt'
  | 'onSignOut'
  | 'user'
  | 'userLinks'
  | 'languageSelectorOptions'
  | 'shouldSuppressHonorsAccountLinks'
  | 'shouldSuppressFindStay'
  | 'shouldSuppressJoinAndSignIn'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'shouldOpenJoinInNewWindow'
  | 'shouldOpenJoinInPage'
  | 'brand'
  | 'isTranslucent'
  | 'onGlobalNavigationClick'
  | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
  | 'analytics'
>;

export function UserNav({
  loginOptions,
  onSignInAttempt,
  onSignOut,
  user,
  languageSelectorOptions,
  userLinks,
  shouldSuppressHonorsAccountLinks,
  shouldSuppressFindStay,
  shouldSuppressJoinAndSignIn,
  shouldOpenAccountLinksInNewWindow,
  shouldOpenJoinInNewWindow,
  shouldOpenJoinInPage,
  brand,
  isTranslucent,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
  analytics,
}: UserNavProps) {
  const [t] = useTranslation(['osc-header', 'osc-login']);

  const defaultOptions: React.ComponentProps<typeof LoginForm> = {
    onLoginAttempt: onSignInAttempt,
    sourceId: 'OHW',
    isInModal: true,
    analytics,
    isLegacySignInAnalyticsEnabled: analytics?.isLegacySignInAnalyticsEnabled,
  };

  const userButtonLinkRef = useRef<HTMLButtonElement>(null);

  const { isShowSpeechBubble, onDialogOpen } = useContext(SpeechBubbleContext);

  const hasBothNavLinks = !!userLinks?.signUpLink && !!userLinks?.signInLink;

  return (
    <nav className="relative ms-auto flex min-h-[4.5rem]" aria-label={t('hiltonHonors')}>
      <ul className="flex">
        {languageSelectorOptions ? (
          <li className="osc-nav-list-item text--sm group flex h-full items-center border-t-4 border-y-transparent px-1">
            <LanguageSelector {...languageSelectorOptions} wrapperClassName="h-full" />
          </li>
        ) : null}
        {userLinks?.findStayLink && !shouldSuppressFindStay ? (
          <NavItemWithLink
            onClick={() => {
              sendInteractionReward();
              sendReward('find-stay-click');
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.FindStay,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            url={userLinks?.findStayLink.url}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
            showNewWindowIcon={false}
            section="user"
            isWide
            namedIcon="calendar-search"
            label={t('yourStays')}
          />
        ) : null}

        {user ? (
          <Popup>
            <NavItemWithPopup
              section="user"
              popupButtonRef={userButtonLinkRef}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberName,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
                sendReward('profile-name-click');
              }}
              label={t('greetings', { username: user.name })}
              icon="user-circle"
              surfaceType={mapBrandCodeToSubmenuSurface(brand.code)}
              isTranslucent={isTranslucent}
            >
              <UserPopupContents
                user={user}
                userLinks={userLinks}
                shouldOpenAccountLinksInNewWindow={shouldOpenAccountLinksInNewWindow}
                shouldSuppressHonorsAccountLinks={shouldSuppressHonorsAccountLinks}
                onSignOut={onSignOut}
                onGlobalNavigationClick={onGlobalNavigationClick}
                isLegacyGlobalNavigationClickAnalyticsEnabled={
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                }
              />
            </NavItemWithPopup>
          </Popup>
        ) : (userLinks?.signInLink || userLinks?.signUpLink) && !shouldSuppressJoinAndSignIn ? (
          <>
            {userLinks?.signUpLink ? (
              !shouldOpenJoinInPage ? (
                <NavItem
                  data-testid="with-modal"
                  className={cx('osc-nav-item-text', {
                    'osc-nav-list-divider relative': hasBothNavLinks,
                  })}
                >
                  <JoinModal
                    analytics={analytics}
                    language={languageSelectorOptions?.locale || 'en'}
                    onLoginAttempt={onSignInAttempt}
                    dialogTrigger={
                      <button
                        data-testid="with-modal"
                        type="button"
                        className="text--sm group flex h-full items-center whitespace-nowrap px-1"
                        onClick={() => {
                          sendInteractionReward();
                          sendReward('nav-join-click');
                          dispatchGlobalNavigationClick(
                            HEADER_CLICK_IDS.Join,
                            onGlobalNavigationClick,
                            isLegacyGlobalNavigationClickAnalyticsEnabled
                          );
                        }}
                      >
                        <NavItemVisual label={t('signUp')} section="user" />
                      </button>
                    }
                    onSuccessfulLogin={() => {
                      setTimeout(() => userButtonLinkRef.current?.focus(), 1000);
                    }}
                  />
                </NavItem>
              ) : (
                <NavItemWithLink
                  section="user"
                  {...userLinks.signUpLink}
                  underline={false}
                  isNewWindow={shouldOpenJoinInNewWindow}
                  showNewWindowIcon={false}
                  label={t('signUp')}
                  onClick={() => {
                    sendInteractionReward();
                    sendReward('nav-join-click');
                    dispatchGlobalNavigationClick(
                      HEADER_CLICK_IDS.Join,
                      onGlobalNavigationClick,
                      isLegacyGlobalNavigationClickAnalyticsEnabled
                    );
                  }}
                  navItemClassName={cx({ 'osc-nav-list-divider relative': hasBothNavLinks })}
                />
              )
            ) : null}

            {userLinks?.signInLink && !isShowSpeechBubble ? (
              <NavItemWithModal
                section="user"
                onLoginAttempt={defaultOptions.onLoginAttempt}
                analytics={analytics}
                loggedInButtonRef={userButtonLinkRef}
                onClick={() => {
                  dispatchGlobalNavigationClick(
                    HEADER_CLICK_IDS.SignIn,
                    onGlobalNavigationClick,
                    isLegacyGlobalNavigationClickAnalyticsEnabled
                  );
                  sendReward('nav-sign-in-click');
                }}
                buttonIcon="user-circle"
                buttonLabel={t('osc-login:signIn')}
                navItemClassName={cx({ 'ms-[0.2ex]': hasBothNavLinks })}
                shouldOpenJoinInPage={shouldOpenJoinInPage}
                locale={languageSelectorOptions?.locale || 'en'}
              >
                <LoginForm {...defaultOptions} {...loginOptions} />
              </NavItemWithModal>
            ) : null}

            {userLinks?.signInLink && isShowSpeechBubble ? (
              <li
                className={cx('osc-nav-list-item text--sm flex h-full items-center px-1 pt-1', {
                  'ms-[0.2ex]': hasBothNavLinks,
                })}
              >
                <NavItemWithSpeechBubble
                  loggedInButtonRef={userButtonLinkRef}
                  {...defaultOptions}
                  analytics={analytics}
                  buttonClassName="text--sm flex h-full items-center whitespace-nowrap px-1"
                  onClick={() => {
                    dispatchGlobalNavigationClick(
                      HEADER_CLICK_IDS.SignIn,
                      onGlobalNavigationClick,
                      isLegacyGlobalNavigationClickAnalyticsEnabled
                    );
                  }}
                  shouldOpenJoinInPage={shouldOpenJoinInPage}
                  locale={languageSelectorOptions?.locale || 'en'}
                  onDialogOpen={onDialogOpen}
                  {...loginOptions}
                >
                  <NavItemVisual label={t('osc-login:signIn')} icon="user-circle" section="user" />
                </NavItemWithSpeechBubble>
              </li>
            ) : null}
          </>
        ) : null}
      </ul>
    </nav>
  );
}

type MainNavProps = Required<Pick<MegaMenuProps, 'mainNavLinks' | 'brand'>> &
  Pick<
    MegaMenuProps,
    | 'heading'
    | 'globalNavLinks'
    | 'surfaceType'
    | 'isTranslucent'
    | 'isHamburgerNavVariant'
    | 'onGlobalNavigationClick'
    | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
  >;
export function MainNav({
  mainNavLinks,
  brand,
  isHamburgerNavVariant,
  heading,
  surfaceType,
  isTranslucent,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
}: MainNavProps) {
  const { locale = 'en' } = useRouter();
  return (
    <div className="flex min-h-[4.5rem] items-center">
      <div className="size-full overflow-x-visible">
        <div className="flex h-full items-center">
          {isHamburgerNavVariant && (
            <HeaderLogoLink
              surfaceType={surfaceType}
              brand={brand}
              className="pe-4 ps-1"
              heading={heading}
            />
          )}
          <ul className="flex h-full items-center rtl:space-x-reverse">
            {mainNavLinks.map(({ label, subMenu, link, onClick }) => {
              if (!subMenu?.length && link?.url) {
                return (
                  <NavItemWithLink
                    {...link}
                    url={replaceLang(link?.url, locale)}
                    section="navigation"
                    label={label}
                    isWide
                    key={label}
                    onClick={(event) => {
                      sendInteractionReward();
                      dispatchGlobalNavigationClickBasedOnLabel(
                        label,
                        undefined,
                        onGlobalNavigationClick,
                        isLegacyGlobalNavigationClickAnalyticsEnabled
                      );
                      onClick?.();
                      link?.onClick?.(event);
                    }}
                  />
                );
              }
              if (subMenu?.length) {
                return (
                  <Popup key={label}>
                    <NavItemWithPopup
                      section="navigation"
                      label={label}
                      indicator="arrowhead-small-up"
                      rotateOnOpen
                      onClick={() => {
                        dispatchGlobalNavigationClickBasedOnLabel(
                          label,
                          undefined,
                          onGlobalNavigationClick,
                          isLegacyGlobalNavigationClickAnalyticsEnabled
                        );
                      }}
                      experimentationConfiguration={link?.experimentationConfiguration}
                      surfaceType={mapBrandCodeToSubmenuSurface(brand.code)}
                      isTranslucent={isTranslucent}
                    >
                      <NavigationPopupContents
                        subMenu={subMenu}
                        label={label}
                        onGlobalNavigationClick={onGlobalNavigationClick}
                        isLegacyGlobalNavigationClickAnalyticsEnabled={
                          isLegacyGlobalNavigationClickAnalyticsEnabled
                        }
                      />
                    </NavItemWithPopup>
                  </Popup>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MegaMenu;
